import api from '@api/api';
import storage from '@api/storage';
import tos from '@api/tos';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store';

export type LoginState = {
  token: string | null;
  loading: boolean;
  error: string;
};

const initialState: LoginState = {
  token: storage.token,
  loading: false,
  error: '',
};

export const tosLogin = createAsyncThunk<{ token: string }, { username: string; password: string }, { rejectValue: string }>(
  'login/tosLogin',
  async ({ username, password }: { username: string; password: string }, { rejectWithValue }) => {
    try {
      const token = await tos.login(username, password);
      await api.verifyCustomer(token);
      return { token };
    } catch (error) {
      if (error.response?.data?.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const loginSlice = createSlice({
  name: 'login',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    logout: (state, action: PayloadAction<string | undefined>) => {
      state.token = null;
      storage.token = null;
      if (action.payload) state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(tosLogin.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(tosLogin.fulfilled, (state, action) => {
      state.loading = false;
      state.token = action.payload.token;
      storage.token = action.payload.token;
    });
    builder.addCase(tosLogin.rejected, (state, action) => {
      state.loading = false;
      storage.token = null;
      if (action.payload) state.error = action.payload;
    });
  },
});

export const { logout } = loginSlice.actions;

export const isAuthenticated = (state: RootState) => Boolean(state.login.token);
export const getLoginState = (state: RootState) => state.login;
export const getToken = (state: RootState) => state.login.token;

export default loginSlice.reducer;
